import React, { useState, useEffect } from 'react';
import { Search, Filter } from 'lucide-react';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import TalentCard from '../../components/public/cards/TalentCard';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import SEO from '../../components/shared/SEO';
import { getAllTalent } from '../../services/talent';
import './Talent.css';

const Talent = () => {
  const [talent, setTalent] = useState([]);
  const [filteredTalent, setFilteredTalent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadTalent();
  }, []);

  useEffect(() => {
    filterAndSortTalent();
  }, [searchTerm, filterCategory, sortBy, talent]);

  const loadTalent = async () => {
    setLoading(true);
    const result = await getAllTalent();
    if (result.success) {
      setTalent(result.data);
      setFilteredTalent(result.data);
    }
    setLoading(false);
  };

  const filterAndSortTalent = () => {
    let filtered = [...talent];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(person =>
        person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (person.skills && person.skills.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(person => person.category === filterCategory);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'rate-low':
          return parseFloat(a.rate) - parseFloat(b.rate);
        case 'rate-high':
          return parseFloat(b.rate) - parseFloat(a.rate);
        default:
          return 0;
      }
    });

    setFilteredTalent(filtered);
  };

  const categories = [...new Set(talent.map(person => person.category))];

  return (
    <div className="public-talent-page">
      <SEO
        title="Hire Film Crew & Talent"
        description="Hire experienced film professionals in Chicago. Directors, cinematographers, gaffers, producers, and crew for your production."
        keywords="hire film crew Chicago, cinematographer for hire, director of photography, gaffer, film producer, production crew"
        url="/talent"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Talent', url: '/talent' }
        ]}
        schema={{
          '@type': 'Service',
          'name': 'Film Crew & Talent Services',
          'provider': {
            '@type': 'LocalBusiness',
            'name': 'IZZISHOWIN'
          },
          'serviceType': 'Film Production Staffing',
          'areaServed': {
            '@type': 'City',
            'name': 'Chicago'
          },
          'description': 'Hire experienced directors, cinematographers, gaffers, producers, and crew for your film production in Chicago.'
        }}
      />
      <PublicNavbar />

      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Professional Talent</h1>
          <p className="page-subtitle">
            Book experienced professionals for your production
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* Filters */}
          <div className="filters-bar">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search talent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="sort-box">
              <Filter size={20} />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="sort-select"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div className="sort-box">
              <Filter size={20} />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="name">Sort by Name</option>
                <option value="rate-low">Rate: Low to High</option>
                <option value="rate-high">Rate: High to Low</option>
              </select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <LoadingSpinner />
          ) : filteredTalent.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-title">No talent found</h3>
              <p className="empty-text">
                {searchTerm || filterCategory !== 'all'
                  ? 'Try adjusting your search criteria'
                  : 'Check back soon for new talent'}
              </p>
            </div>
          ) : (
            <>
              <div className="results-count">
                Showing {filteredTalent.length} professional{filteredTalent.length !== 1 ? 's' : ''}
              </div>
              <div className="talent-grid">
                {filteredTalent.map((person) => (
                  <TalentCard key={person.id} talent={person} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Talent;